/**
 * EasyGTM API Utilities
 *
 * This module provides utilities for interacting with EasyGTM backend
 * and retrieving user context for MAARAN AI integration.
 */

import Cookies from "js-cookie";
import instance from "@/network";

// Token cookie name
const MAARAN_TOKEN_KEY = "maaran_api_token";

export const generateMaaranToken = async () => {
  try {
    const response = await instance.post("/api/user/token");
    const data = response?.data;

    // Handle response format: { data: { token: "..." } } or { token: "..." }
    const token = data?.data?.token || data?.token || data;

    if (token && typeof token === "string") {
      // Store token in cookie (expires in 30 days)
      Cookies.set(MAARAN_TOKEN_KEY, token, {
        expires: 1,
        path: "/",
        secure: process.env.NODE_ENV === "production",
      });
      return token;
    }

    return null;
  } catch (error) {
    console.error("Failed to generate MAARAN token:", error);
    return null;
  }
};

export const getMaaranToken = () => {
  return Cookies.get(MAARAN_TOKEN_KEY) || null;
};

export const hasMaaranToken = () => {
  return !!Cookies.get(MAARAN_TOKEN_KEY);
};

export const clearMaaranToken = () => {
  Cookies.remove(MAARAN_TOKEN_KEY);
};

export const setMaaranToken = (token) => {
  if (token) {
    Cookies.set(MAARAN_TOKEN_KEY, token, {
      expires: 30,
      path: "/",
      secure: process.env.NODE_ENV === "production",
    });
  }
};

export const getCurrentUser = () => {
  try {
    const userCookie = Cookies.get("user");
    if (userCookie) {
      return JSON.parse(userCookie);
    }
  } catch (error) {
    console.error("Failed to parse user cookie:", error);
  }
  return null;
};

export const getUserId = () => {
  const user = getCurrentUser();
  return user?.id?.toString() || null;
};

export const getCurrentOrganization = () => {
  try {
    const orgCookie = Cookies.get("organization");
    if (orgCookie) {
      return JSON.parse(orgCookie);
    }
  } catch (error) {
    console.error("Failed to parse organization cookie:", error);
  }
  return null;
};

export const getOrganizationId = () => {
  const org = getCurrentOrganization();
  return org?.id?.toString() || null;
};

export const getBrandId = () => {
  // Try to get from localStorage (where Redux might persist)
  try {
    const brandData = localStorage.getItem("selectedBrand");
    if (brandData) {
      const brand = JSON.parse(brandData);
      return brand?.id?.toString() || null;
    }
  } catch (error) {
    console.error("Failed to get brand ID:", error);
  }

  // Fallback: Get first brand from user's brands if available
  const user = getCurrentUser();
  if (user?.relationships?.brands?.data?.[0]?.id) {
    return user.relationships.brands.data[0].id.toString();
  }

  return null;
};

export const getMaaranUserContext = () => {
  const token = getMaaranToken();
  const userId = getUserId();
  const brandId = getBrandId();
  const organizationId = getOrganizationId();

  return {
    token,
    userId,
    brandId,
    organizationId,
    isAuthenticated: !!(userId && organizationId),
    hasToken: !!token,
  };
};

export const isAuthenticated = () => {
  const user = getCurrentUser();
  const org = getCurrentOrganization();
  const isLoggedIn = Cookies.get("isLoggedInYN") === "true";

  return !!(user && org && isLoggedIn);
};

export const getAuthHeaders = () => {
  const token = getMaaranToken();
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
  return {};
};

export default {
  // Token management
  generateMaaranToken,
  getMaaranToken,
  hasMaaranToken,
  setMaaranToken,
  clearMaaranToken,
  // User context
  getCurrentUser,
  getUserId,
  getCurrentOrganization,
  getOrganizationId,
  getBrandId,
  getMaaranUserContext,
  isAuthenticated,
  getAuthHeaders,
};
